import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Send, Users, Car, UsersRound, Eye, Link as LinkIcon, Sparkles,
  History, X, MessageCircle, Timer, CheckCircle2, AlertTriangle,
  Target, Search, CheckSquare, Square,
} from "lucide-react";
import { dataApi, formatApiError, api } from "../api/client";

/**
 * NotificationsBroadcastView — Envío masivo por WhatsApp (2chat).
 * Endpoint: POST /api/admin/notifications/broadcast-whatsapp
 * - Personaliza {{name}} y {{link}}.
 * - Cola en background con delay (default 1s) entre envíos.
 * - Polling de progreso cada 3s vía GET /broadcast-whatsapp/{id}.
 */
export default function NotificationsBroadcastView() {
  const DEFAULT_APK = `${process.env.REACT_APP_BACKEND_URL || ""}/api/public/download/passenger-apk`;
  const [bodyText, setBodyText] = useState(
    "🎉 Hola {{name}}! Descarga la app de *Turismo MDD* y disfruta 20% de descuento en tu primera carrera 🚕"
  );
  const [url, setUrl] = useState(DEFAULT_APK);
  const [segment, setSegment] = useState("passengers");
  const [delayMs, setDelayMs] = useState(1000);
  const [preview, setPreview] = useState(null);
  const [running, setRunning] = useState(null);   // {broadcast_id, ...}
  const [progress, setProgress] = useState(null); // doc del audit con status/sent/failed
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const pollRef = useRef(null);

  // v39: Selección quirúrgica de destinatarios individuales.
  // Cuando `selectedPhones.size > 0`, se ignora el segmento y se envía
  // solo a esos teléfonos (backend filtra por phone_digits).
  const [selectedPhones, setSelectedPhones] = useState(new Set());
  const [showPicker, setShowPicker] = useState(false);

  const BODY_MAX = 500;

  const loadHistory = () => dataApi.listBroadcasts().then(setHistory).catch(() => {});
  useEffect(() => { loadHistory(); }, []);

  // Polling de progreso mientras haya un envío corriendo
  useEffect(() => {
    if (!running?.broadcast_id) return;
    let stopped = false;
    const tick = async () => {
      try {
        const p = await dataApi.whatsappBroadcastStatus(running.broadcast_id);
        if (stopped) return;
        setProgress(p);
        if (p.status === "done") {
          loadHistory();
          return;
        }
      } catch { /* ignore */ }
      if (!stopped) pollRef.current = setTimeout(tick, 3000);
    };
    tick();
    return () => { stopped = true; if (pollRef.current) clearTimeout(pollRef.current); };
  }, [running?.broadcast_id]);

  const buildPayload = () => {
    const p = {
      body: bodyText.trim(),
      url: url.trim() || null,
      segment,
      delay_ms: Number(delayMs) || 1000,
    };
    if (selectedPhones.size > 0) {
      p.recipient_phones = Array.from(selectedPhones);
    }
    return p;
  };

  const clearSelection = () => setSelectedPhones(new Set());

  const doPreview = async () => {
    setErr(""); setPreview(null); setRunning(null); setProgress(null); setBusy(true);
    try {
      const r = await dataApi.broadcastWhatsApp2chat({ ...buildPayload(), dry_run: true });
      setPreview(r);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const doSend = async () => {
    if (!preview) { setErr("Primero genera una vista previa"); return; }
    if (preview.total_targets === 0) { setErr("No hay destinatarios con teléfono en este segmento"); return; }
    const secs = Math.round((preview.total_targets * (delayMs / 1000)));
    const mins = Math.round(secs / 60);
    const ok = window.confirm(
      `Enviarás ${preview.total_targets} WhatsApps vía 2chat con ${delayMs}ms de retraso entre cada uno.\n\n` +
      `Tiempo estimado: ${secs < 90 ? secs + "s" : mins + " min"}.\n\n¿Continuar?`
    );
    if (!ok) return;
    setBusy(true); setRunning(null); setProgress(null); setErr("");
    try {
      const r = await dataApi.broadcastWhatsApp2chat(buildPayload());
      setRunning(r);
      setPreview(null);
    } catch (e) { setErr(formatApiError(e)); }
    finally { setBusy(false); }
  };

  const insertVar = (v) => setBodyText((b) => (b + " " + v).slice(0, BODY_MAX));

  const segments = [
    { id: "passengers", label: "Solo Pasajeros", icon: UsersRound, color: "pink" },
    { id: "drivers", label: "Solo Conductores", icon: Car, color: "amber" },
    { id: "all", label: "Todos", icon: Users, color: "blue" },
  ];

  const sampleName = preview?.sample_recipient?.split(" ")[0] || "Ana";
  const previewBody = (preview?.preview?.body)
    || bodyText.replace("{{name}}", sampleName).replace("{{link}}", url || "")
       + (url && !bodyText.includes("{{link}}") && !bodyText.includes(url) ? `\n\n${url}` : "");

  return (
    <div className="p-6 space-y-4" data-testid="broadcast-view">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display font-bold text-2xl flex items-center gap-2">
            <MessageCircle className="w-6 h-6 text-emerald-600" /> WhatsApp masivo (2chat)
          </h1>
          <p className="text-sm text-neutral-500 mt-1">
            Envía mensajes personalizados vía WhatsApp a tu lista de pasajeros y conductores usando la API de 2chat.
          </p>
        </div>
        <button onClick={() => setShowHistory(true)}
                className="px-4 py-2 border border-neutral-300 rounded-lg text-sm font-semibold flex items-center gap-2 hover:bg-neutral-50"
                data-testid="broadcast-history-btn">
          <History className="w-4 h-4" /> Historial ({history.length})
        </button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* ─── Editor ─── */}
        <div className="space-y-3">
          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-2">Segmento</label>
            <div className="grid grid-cols-3 gap-2">
              {segments.map((s) => (
                <button
                  key={s.id}
                  onClick={() => { setSegment(s.id); setPreview(null); }}
                  disabled={selectedPhones.size > 0}
                  className={`p-3 border-2 rounded-xl text-center transition ${segment === s.id ? `border-${s.color}-500 bg-${s.color}-50` : "border-neutral-200 hover:border-neutral-300"} ${selectedPhones.size > 0 ? "opacity-40 cursor-not-allowed" : ""}`}
                  data-testid={`segment-${s.id}`}
                >
                  <s.icon className={`w-6 h-6 mx-auto ${segment === s.id ? `text-${s.color}-600` : "text-neutral-400"}`} />
                  <div className="text-xs font-bold mt-1">{s.label}</div>
                </button>
              ))}
            </div>

            {/* v39 · Selección quirúrgica de destinatarios individuales */}
            <div className="mt-2 flex items-center justify-between gap-2 px-3 py-2 rounded-lg border border-dashed border-emerald-300 bg-emerald-50/50">
              <div className="flex items-center gap-2 text-xs text-emerald-900">
                <Target className="w-3.5 h-3.5" />
                {selectedPhones.size === 0 ? (
                  <span>Envío por segmento. Puedes elegir usuarios específicos →</span>
                ) : (
                  <span><b>{selectedPhones.size}</b> destinatario{selectedPhones.size !== 1 ? "s" : ""} seleccionado{selectedPhones.size !== 1 ? "s" : ""} (segmento ignorado)</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                {selectedPhones.size > 0 && (
                  <button onClick={() => { clearSelection(); setPreview(null); }}
                          className="text-[11px] text-red-600 font-semibold hover:underline"
                          data-testid="broadcast-clear-selection">
                    Limpiar
                  </button>
                )}
                <button onClick={() => setShowPicker(true)}
                        className="text-xs bg-emerald-600 text-white font-bold px-3 py-1.5 rounded hover:bg-emerald-700"
                        data-testid="broadcast-open-picker">
                  Elegir usuarios
                </button>
              </div>
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between mb-1.5">
              <label className="text-xs font-semibold uppercase tracking-wider text-neutral-500">Mensaje WhatsApp</label>
              <div className="flex items-center gap-2">
                <button onClick={() => insertVar("{{name}}")}
                        className="text-xs text-indigo-600 font-semibold hover:underline flex items-center gap-1"
                        data-testid="broadcast-insert-name">
                  <Sparkles className="w-3 h-3" /> {"{{name}}"}
                </button>
                <button onClick={() => insertVar("{{link}}")}
                        className="text-xs text-indigo-600 font-semibold hover:underline flex items-center gap-1"
                        data-testid="broadcast-insert-link">
                  <LinkIcon className="w-3 h-3" /> {"{{link}}"}
                </button>
              </div>
            </div>
            <textarea
              value={bodyText}
              onChange={(e) => setBodyText(e.target.value.slice(0, BODY_MAX))}
              maxLength={BODY_MAX}
              rows={6}
              className="office-input font-mono text-sm"
              data-testid="broadcast-body-input"
              placeholder="Hola {{name}}, descarga la app aquí: {{link}}"
            />
            <div className="flex justify-between text-[10px] text-neutral-400 mt-1">
              <span>
                Formato WhatsApp: <code className="bg-neutral-100 px-1 rounded">*negrita*</code>,{" "}
                <code className="bg-neutral-100 px-1 rounded">_cursiva_</code>. Personaliza con{" "}
                <code className="bg-neutral-100 px-1 rounded">{"{{name}}"}</code>.
              </span>
              <span className={bodyText.length >= BODY_MAX ? "text-red-600 font-semibold" : ""}>
                {bodyText.length}/{BODY_MAX}
              </span>
            </div>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
              <LinkIcon className="w-3.5 h-3.5 inline mr-1" /> Link de descarga (se anexa al final si no está en el mensaje)
            </label>
            <input value={url} onChange={(e) => setUrl(e.target.value)}
                   className="office-input font-mono text-xs" placeholder="https://…" data-testid="broadcast-url-input"/>
            <button onClick={() => setUrl(DEFAULT_APK)} className="text-[10px] text-indigo-600 mt-1 hover:underline" data-testid="broadcast-url-reset">
              ↻ Usar link del APK Pasajero
            </button>
          </div>

          <div>
            <label className="block text-xs font-semibold uppercase tracking-wider text-neutral-500 mb-1.5">
              <Timer className="w-3.5 h-3.5 inline mr-1" /> Retraso entre envíos (ms)
            </label>
            <div className="flex items-center gap-2">
              <input
                type="number" min={200} max={10000} step={100}
                value={delayMs}
                onChange={(e) => setDelayMs(Math.max(200, Math.min(10000, Number(e.target.value) || 1000)))}
                className="office-input max-w-[140px]"
                data-testid="broadcast-delay-input"
              />
              <span className="text-xs text-neutral-500">
                = {(delayMs / 1000).toFixed(1)}s por mensaje.
                {preview?.total_targets > 0 && (
                  <> Total estimado: <b>{Math.round(preview.total_targets * (delayMs / 1000) / 60)} min</b> para {preview.total_targets} envíos.</>
                )}
              </span>
            </div>
            <p className="text-[10px] text-neutral-400 mt-1">
              Recomendado 1000ms para no ser bloqueado por WhatsApp con listas grandes.
            </p>
          </div>

          {err && (
            <div className="chip chip-danger flex items-center gap-2" data-testid="broadcast-error">
              <AlertTriangle className="w-3.5 h-3.5"/> {err}
            </div>
          )}

          <div className="flex gap-2 pt-2">
            <button onClick={doPreview} disabled={busy || bodyText.trim().length < 3}
                    className="flex-1 py-3 border-2 border-emerald-500 text-emerald-700 rounded-lg font-bold hover:bg-emerald-50 disabled:opacity-50"
                    data-testid="broadcast-preview-btn">
              <Eye className="w-4 h-4 inline mr-1" /> {busy ? "..." : "Vista previa"}
            </button>
            <button
              onClick={doSend}
              disabled={busy || !preview || preview.total_targets === 0 || !!running}
              className="flex-[2] py-3 bg-emerald-600 text-white rounded-lg font-bold flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-emerald-700"
              data-testid="broadcast-send-btn"
            >
              <MessageCircle className="w-4 h-4" />
              {busy ? "Encolando..." :
                running ? "Enviando..." :
                preview
                  ? `Enviar a ${preview.total_targets} vía WhatsApp (2chat)`
                  : "Enviar vía WhatsApp (2chat)"}
            </button>
          </div>
        </div>

        {/* ─── Preview & Progress ─── */}
        <div className="space-y-3">
          <div className="bg-gradient-to-b from-emerald-800 to-emerald-950 rounded-2xl p-5 shadow-xl" data-testid="wa-phone-preview">
            <div className="text-white/60 text-xs mb-3 text-center font-mono">📱 VISTA PREVIA WHATSAPP</div>
            <div className="bg-white rounded-xl p-3 shadow max-w-[85%]">
              <div className="text-emerald-700 text-[10px] font-bold mb-1">Turismo MDD</div>
              <div className="whitespace-pre-wrap text-neutral-800 text-sm leading-snug break-words" data-testid="preview-body">
                {previewBody || <span className="opacity-50 italic">Escribe un mensaje</span>}
              </div>
              <div className="text-right text-[10px] text-neutral-400 mt-1">ahora ✓✓</div>
            </div>
          </div>

          {preview && !running && (
            <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4" data-testid="broadcast-preview-panel">
              <div className="font-bold text-emerald-900 mb-1 text-sm">Destinatarios</div>
              <div className="text-3xl font-black text-emerald-700" data-testid="preview-total">{preview.total_targets}</div>
              <div className="text-xs text-emerald-700 mt-0.5">
                {segment === "passengers" ? "pasajeros" : segment === "drivers" ? "conductores" : "usuarios"} con teléfono registrado
                {preview.personalization && <span> · <strong>Personalizado</strong> con {"{{name}}"}</span>}
              </div>
              <div className="text-[11px] text-emerald-800/70 mt-2 flex items-center gap-1">
                <Timer className="w-3 h-3" /> Duración estimada: <b>{Math.round(preview.estimated_seconds / 60)} min</b>
              </div>
              {preview.total_targets === 0 && (
                <div className="mt-2 text-xs text-amber-700 bg-amber-50 p-2 rounded">
                  ⚠️ Ningún destinatario tiene teléfono en este segmento. Importa números vía CSV en Usuarios primero.
                </div>
              )}
            </div>
          )}

          {(running || progress) && (
            <div className="bg-white border-2 border-emerald-400 rounded-lg p-4" data-testid="broadcast-progress-panel">
              <div className="flex items-center justify-between mb-2">
                <div className="font-bold text-emerald-800 flex items-center gap-2">
                  <Send className="w-4 h-4" /> Envío en curso · 2chat
                </div>
                <span className={`text-[10px] font-mono uppercase px-2 py-0.5 rounded ${
                  progress?.status === "done" ? "bg-emerald-100 text-emerald-700" :
                  progress?.status === "running" ? "bg-amber-100 text-amber-700 animate-pulse" :
                  "bg-neutral-100 text-neutral-500"
                }`}>
                  {progress?.status || running?.status || "queued"}
                </span>
              </div>
              <ProgressBar
                processed={progress?.processed_count || 0}
                total={progress?.total_targets || running?.total_targets || 0}
              />
              <div className="grid grid-cols-3 gap-2 text-center mt-3">
                <StatTile
                  color="emerald" label="Enviadas"
                  value={progress?.sent_count ?? 0}
                  testid="progress-sent"
                />
                <StatTile
                  color="neutral" label="Total"
                  value={progress?.total_targets ?? running?.total_targets ?? 0}
                  testid="progress-total"
                />
                <StatTile
                  color={(progress?.failed_count || 0) > 0 ? "red" : "neutral"}
                  label="Fallidas"
                  value={progress?.failed_count ?? 0}
                  testid="progress-failed"
                />
              </div>
              {progress?.status === "done" && (
                <div className="mt-3 text-xs text-emerald-800 bg-emerald-50 rounded p-2 flex items-center gap-1.5" data-testid="progress-done">
                  <CheckCircle2 className="w-3.5 h-3.5"/> Envío completado.
                </div>
              )}
              {Array.isArray(progress?.errors) && progress.errors.length > 0 && (
                <details className="mt-2 text-xs">
                  <summary className="cursor-pointer text-red-600 font-semibold">
                    Ver primeros {progress.errors.length} errores
                  </summary>
                  <div className="mt-1 max-h-40 overflow-y-auto space-y-1 font-mono text-[10px] text-neutral-700 bg-neutral-50 p-2 rounded">
                    {progress.errors.map((e, i) => (
                      <div key={i}>· {e.phone || e.user_id || "?"} — {typeof e.detail === "object" ? JSON.stringify(e.detail).slice(0, 120) : (e.error || e.reason || "error")}</div>
                    ))}
                  </div>
                </details>
              )}
            </div>
          )}
        </div>
      </div>

      {showHistory && <HistoryModal history={history} onClose={() => setShowHistory(false)} />}
      {showPicker && (
        <RecipientPickerModal
          initialSelected={selectedPhones}
          onClose={() => setShowPicker(false)}
          onSave={(next) => { setSelectedPhones(next); setPreview(null); setShowPicker(false); }}
        />
      )}
    </div>
  );
}

function ProgressBar({ processed, total }) {
  const pct = total > 0 ? Math.min(100, Math.round((processed / total) * 100)) : 0;
  return (
    <div>
      <div className="flex items-center justify-between text-[11px] font-mono text-neutral-600 mb-1">
        <span>{processed}/{total}</span>
        <span>{pct}%</span>
      </div>
      <div className="h-2 bg-neutral-100 rounded-full overflow-hidden">
        <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${pct}%` }} data-testid="progress-bar"/>
      </div>
    </div>
  );
}

function StatTile({ color, label, value, testid }) {
  const cls = {
    emerald: "bg-emerald-50 text-emerald-700",
    neutral: "bg-neutral-50 text-neutral-700",
    red: "bg-red-50 text-red-700",
  }[color] || "bg-neutral-50 text-neutral-700";
  return (
    <div className={`rounded p-2 ${cls}`} data-testid={testid}>
      <div className="text-2xl font-black">{value}</div>
      <div className="text-[10px] font-bold uppercase opacity-70">{label}</div>
    </div>
  );
}

function HistoryModal({ history, onClose }) {
  const [metrics, setMetrics] = useState(null);
  const [loadingMetrics, setLoadingMetrics] = useState(false);

  const openMetrics = async (h) => {
    setLoadingMetrics(true); setMetrics({ h, data: null });
    try {
      const { data } = await api.get(`/admin/notifications/broadcasts/${h.id}/metrics`);
      setMetrics({ h, data });
    } catch (e) { setMetrics({ h, error: e?.response?.data?.detail || e.message }); }
    finally { setLoadingMetrics(false); }
  };

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl p-6 w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-bold text-xl">Historial de envíos</h2>
          <button onClick={onClose}><X className="w-5 h-5" /></button>
        </div>
        <div className="flex-1 overflow-auto space-y-2">
          {history.length === 0 ? (
            <div className="p-8 text-center text-sm text-neutral-500">Sin envíos aún</div>
          ) : history.map((h) => {
            const isWa = h.kind === "whatsapp_broadcast";
            return (
              <div key={h.id} className="border border-neutral-200 rounded-lg p-3">
                <div className="flex items-start justify-between gap-2">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className={`text-[10px] font-mono uppercase px-1.5 py-0.5 rounded ${isWa ? "bg-emerald-100 text-emerald-700" : "bg-indigo-100 text-indigo-700"}`}>
                        {isWa ? "WhatsApp · 2chat" : "Push · OneSignal"}
                      </span>
                      <div className="font-bold text-sm truncate">{h.title || (isWa ? "WhatsApp" : "Push")}</div>
                    </div>
                    <div className="text-xs text-neutral-600 truncate mt-0.5">{h.body}</div>
                    <div className="text-[10px] text-neutral-400 mt-1">
                      {new Date(h.created_at).toLocaleString("es-PE")} · {h.segment} · por {h.sent_by_name || "?"}
                      {isWa && h.status && <> · <b>{h.status}</b></>}
                    </div>
                  </div>
                  <div className="flex flex-col items-end gap-1 flex-shrink-0">
                    <span className="bg-emerald-100 text-emerald-700 font-bold px-2 py-0.5 rounded text-xs">
                      {h.sent_count}/{h.total_targets}
                    </span>
                    {!isWa && (
                      <button onClick={() => openMetrics(h)} className="text-[10px] text-indigo-600 hover:underline font-semibold" data-testid={`broadcast-metrics-${h.id}`}>
                        Ver métricas →
                      </button>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
        {metrics && (
          <div className="mt-3 border-t pt-3">
            <div className="flex items-center justify-between mb-2">
              <div className="font-bold text-sm truncate">📊 ROI: {metrics.h.title}</div>
              <button onClick={() => setMetrics(null)} className="text-xs text-neutral-500">Cerrar</button>
            </div>
            {loadingMetrics ? <div className="text-xs text-neutral-500">Cargando...</div> :
             metrics.error ? <div className="chip chip-danger">{metrics.error}</div> :
             metrics.data && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-center">
                <div className="bg-blue-50 rounded p-2">
                  <div className="text-xl font-black text-blue-700">{metrics.data.sent_count}</div>
                  <div className="text-[9px] font-bold uppercase text-blue-600">Enviadas</div>
                </div>
                <div className="bg-purple-50 rounded p-2">
                  <div className="text-xl font-black text-purple-700">{metrics.data.unique_opens}</div>
                  <div className="text-[9px] font-bold uppercase text-purple-600">Abrieron ({metrics.data.open_rate_pct}%)</div>
                </div>
                <div className="bg-pink-50 rounded p-2">
                  <div className="text-xl font-black text-pink-700">{metrics.data.rides_attributed}</div>
                  <div className="text-[9px] font-bold uppercase text-pink-600">Carreras ({metrics.data.conversion_rate_pct}%)</div>
                </div>
                <div className="bg-emerald-50 rounded p-2">
                  <div className="text-xl font-black text-emerald-700">S/ {metrics.data.revenue_attributed}</div>
                  <div className="text-[9px] font-bold uppercase text-emerald-600">Ingresos 72h</div>
                </div>
              </div>
             )}
          </div>
        )}
      </div>
    </div>
  );
}


/**
 * v39 · Modal para elegir destinatarios individuales por teléfono.
 * Reutiliza el estilo tabla+buscador de UsersManagement.
 */
function RecipientPickerModal({ initialSelected, onClose, onSave }) {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [roleFilter, setRoleFilter] = useState("all"); // all | passenger | driver
  const [selected, setSelected] = useState(new Set(initialSelected || []));

  useEffect(() => {
    dataApi.users()
      .then((list) => {
        // Solo usuarios activos con teléfono válido — los que realmente pueden recibir WhatsApp.
        const eligible = (Array.isArray(list) ? list : [])
          .filter((u) => u.is_active !== false && typeof u.phone === "string" && u.phone.trim());
        setUsers(eligible);
      })
      .catch(() => setUsers([]))
      .finally(() => setLoading(false));
  }, []);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return users.filter((u) => {
      if (roleFilter === "passenger" && u.role !== "passenger") return false;
      if (roleFilter === "driver" && u.role !== "driver" && u.role !== "socio") return false;
      if (!needle) return true;
      const hay = [u.name, u.phone, u.email].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(needle);
    });
  }, [users, q, roleFilter]);

  const toggle = (phone) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(phone)) next.delete(phone);
      else next.add(phone);
      return next;
    });
  };

  const toggleAllFiltered = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      const allSelected = filtered.every((u) => next.has(u.phone));
      if (allSelected) filtered.forEach((u) => next.delete(u.phone));
      else filtered.forEach((u) => next.add(u.phone));
      return next;
    });
  };

  const allFilteredSelected = filtered.length > 0 && filtered.every((u) => selected.has(u.phone));
  const filters = [
    { id: "all",       label: "Todos",       icon: Users },
    { id: "passenger", label: "Pasajeros",   icon: UsersRound },
    { id: "driver",    label: "Conductores", icon: Car },
  ];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-xl w-full max-w-3xl max-h-[85vh] flex flex-col" onClick={(e) => e.stopPropagation()} data-testid="broadcast-picker-modal">
        <div className="p-5 border-b border-neutral-200 flex items-center justify-between">
          <div>
            <h2 className="font-bold text-xl flex items-center gap-2">
              <Target className="w-5 h-5 text-emerald-600" /> Seleccionar destinatarios
            </h2>
            <p className="text-xs text-neutral-500 mt-1">
              Elige uno o más usuarios. Solo se envía a los que estén activos y tengan teléfono.
            </p>
          </div>
          <button onClick={onClose} data-testid="broadcast-picker-close"><X className="w-5 h-5" /></button>
        </div>

        <div className="p-4 space-y-3 border-b border-neutral-200">
          <div className="relative">
            <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-neutral-400" />
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Buscar por nombre, teléfono o correo…"
              className="w-full pl-9 pr-3 py-2 border border-neutral-300 rounded-lg text-sm"
              data-testid="broadcast-picker-search"
              autoFocus
            />
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            {filters.map((f) => (
              <button key={f.id}
                onClick={() => setRoleFilter(f.id)}
                className={`px-3 py-1.5 rounded-full text-xs font-bold flex items-center gap-1 border ${
                  roleFilter === f.id
                    ? "bg-emerald-600 text-white border-emerald-600"
                    : "bg-white text-neutral-600 border-neutral-300 hover:bg-neutral-50"
                }`}
                data-testid={`broadcast-picker-role-${f.id}`}
              >
                <f.icon className="w-3 h-3" /> {f.label}
              </button>
            ))}
            <div className="ml-auto text-xs text-neutral-500">
              Mostrando {filtered.length} · Seleccionados {selected.size}
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-auto">
          {loading ? (
            <div className="p-8 text-center text-sm text-neutral-500">Cargando usuarios…</div>
          ) : filtered.length === 0 ? (
            <div className="p-8 text-center text-sm text-neutral-500">Sin resultados</div>
          ) : (
            <table className="w-full text-sm">
              <thead className="bg-neutral-50 sticky top-0">
                <tr className="text-left text-[11px] uppercase text-neutral-500">
                  <th className="px-4 py-2 w-10">
                    <button onClick={toggleAllFiltered} data-testid="broadcast-picker-toggle-all">
                      {allFilteredSelected
                        ? <CheckSquare className="w-4 h-4 text-emerald-600" />
                        : <Square className="w-4 h-4 text-neutral-400" />}
                    </button>
                  </th>
                  <th className="px-2 py-2">Nombre</th>
                  <th className="px-2 py-2">Teléfono</th>
                  <th className="px-2 py-2">Rol</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((u) => {
                  const on = selected.has(u.phone);
                  return (
                    <tr key={u.id} onClick={() => toggle(u.phone)}
                        className={`border-t border-neutral-100 cursor-pointer ${on ? "bg-emerald-50" : "hover:bg-neutral-50"}`}
                        data-testid={`broadcast-picker-row-${u.id}`}>
                      <td className="px-4 py-2">
                        {on
                          ? <CheckSquare className="w-4 h-4 text-emerald-600" />
                          : <Square className="w-4 h-4 text-neutral-400" />}
                      </td>
                      <td className="px-2 py-2 font-semibold">{u.name || "—"}</td>
                      <td className="px-2 py-2 font-mono text-xs">{u.phone}</td>
                      <td className="px-2 py-2">
                        <span className={`text-[10px] uppercase font-bold px-1.5 py-0.5 rounded ${
                          u.role === "passenger" ? "bg-pink-100 text-pink-700" :
                          u.role === "driver" ? "bg-amber-100 text-amber-700" :
                          u.role === "socio" ? "bg-orange-100 text-orange-700" :
                          "bg-neutral-100 text-neutral-600"
                        }`}>{u.role || "?"}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        <div className="p-4 border-t border-neutral-200 flex items-center justify-between gap-3">
          <button onClick={() => setSelected(new Set())}
                  className="text-xs text-red-600 font-semibold hover:underline"
                  data-testid="broadcast-picker-clear">
            Limpiar selección
          </button>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
                    className="px-4 py-2 border border-neutral-300 rounded-lg text-sm font-semibold hover:bg-neutral-50"
                    data-testid="broadcast-picker-cancel">
              Cancelar
            </button>
            <button onClick={() => onSave(selected)}
                    className="px-4 py-2 bg-emerald-600 text-white rounded-lg text-sm font-bold hover:bg-emerald-700 disabled:opacity-50"
                    disabled={selected.size === 0}
                    data-testid="broadcast-picker-save">
              Usar {selected.size} destinatario{selected.size !== 1 ? "s" : ""}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
